/**
 * Document Shares Repository
 *
 * Handles CRUD operations for document sharing functionality:
 * - Creating share links
 * - Listing shares for a building
 * - Revoking shares
 * - Validating share tokens (via Edge Function)
 */

import { supabase } from "@/integrations/supabase/client";
import { buildShareUrl } from "@/utils/shareUrl";
import { track } from "@/lib/posthog";
import { getAllDescendantFolderIds } from "./queries";

// ============================================================================
// Types
// ============================================================================

export interface ShareDocument {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  created_at: string;
  signedUrl: string | null;
}

export interface ShareFolderNode {
  id: string;
  name: string;
  children: ShareFolderNode[];
  documents: ShareDocument[];
}

export interface ShareDetails {
  title: string | null;
  expiresAt: string;
  documentCount: number;
  folderCount: number;
  documents: ShareDocument[];
  folderTree: ShareFolderNode[];
  rootDocuments: ShareDocument[];
}

export interface Share {
  id: string;
  share_token: string;
  title: string | null;
  expires_at: string;
  view_count: number;
  last_accessed_at: string | null;
  is_revoked: boolean;
  created_at: string;
  document_count: number;
}

export interface CreateShareParams {
  documentIds: string[];
  folderIds?: string[];
  buildingId: string;
  orgId: string;
  expiresInSeconds: number;
  title?: string;
}

export interface CreateShareResult {
  shareId: string;
  shareToken: string;
  shareUrl: string;
}

// ============================================================================
// Constants
// ============================================================================

export const EXPIRY_OPTIONS = [
  { label: "1 hour", value: 3600 },
  { label: "24 hours", value: 86400 },
  { label: "7 days", value: 604800 },
  { label: "30 days", value: 2592000 },
] as const;

// ============================================================================
// Functions
// ============================================================================

/**
 * Generate a cryptographically secure share token
 * Uses crypto.getRandomValues() for better security than UUID
 */
function generateShareToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Create a new document share
 *
 * When folders are selected, recursively expands them to include all documents
 * within the folder and all nested subfolders.
 * All documents are stored in document_share_items with source_folder_id tracking:
 * - NULL = directly selected document (shows at root in share view)
 * - folder_id = included via folder expansion (shows in that folder)
 */
export async function createShare(params: CreateShareParams): Promise<CreateShareResult> {
  const { documentIds, folderIds = [], buildingId, expiresInSeconds, title } = params;

  if (documentIds.length === 0 && folderIds.length === 0) {
    throw new Error("At least one document or folder must be selected");
  }

  // The share's org_id MUST match every document's org_id — that's what
  // document_share_items_insert RLS enforces. The UI's currentOrganization
  // can drift from the building/folder the user is actually viewing (the
  // user is a member of multiple orgs and the active context isn't always
  // the one they're acting on), so we derive org_id from the content being
  // shared rather than trusting the param. Since RLS already restricts what
  // the user can see, looking up via the user's session is safe.
  let resolvedOrgId: string | null = null;

  if (folderIds.length > 0) {
    const { data: folderRow, error: folderLookupError } = await supabase
      .from("document_folders")
      .select("org_id")
      .eq("id", parseInt(folderIds[0], 10))
      .maybeSingle();
    if (folderLookupError) {
      console.error("Error resolving folder org:", folderLookupError);
      throw new Error("Failed to resolve folder organisation");
    }
    resolvedOrgId = (folderRow?.org_id as string | null) ?? null;
  }

  if (!resolvedOrgId && documentIds.length > 0) {
    const { data: docRow, error: docLookupError } = await supabase
      .from("documents")
      .select("org_id")
      .eq("id", parseInt(documentIds[0], 10))
      .maybeSingle();
    if (docLookupError) {
      console.error("Error resolving document org:", docLookupError);
      throw new Error("Failed to resolve document organisation");
    }
    resolvedOrgId = (docRow?.org_id as string | null) ?? null;
  }

  if (!resolvedOrgId) {
    throw new Error("Could not determine organisation for the selected items");
  }

  // Track documents with their source folder (null = directly selected)
  // Map: docId -> source_folder_id (null for direct selection)
  const documentSourceMap = new Map<string, number | null>();

  // Resolve folder docs first so they take precedence over the documentIds list.
  // The recursive cascade-selection feature adds every nested doc to
  // selectedDocumentIds when the user toggles a parent folder, so a "directly
  // selected" entry isn't authoritative — if the doc lives in a folder we're
  // explicitly sharing, the folder is its real source and we want the
  // recipient to see folder structure.
  if (folderIds.length > 0) {
    const allFolderIds = await getAllDescendantFolderIds(folderIds.map(Number));

    // Filter to the resolved org so a cross-org doc visible via project
    // assignment can't sneak into the share — document_share_items_insert
    // RLS rejects rows where doc.org_id != share.org_id.
    const { data: folderDocs, error: folderDocsError } = await supabase
      .from("documents")
      .select("id, folder_id")
      .in("folder_id", allFolderIds)
      .eq("org_id", resolvedOrgId)
      .is("deleted_at", null);

    if (folderDocsError) {
      console.error("Error fetching folder documents:", folderDocsError);
      throw new Error("Failed to get documents from selected folders");
    }

    folderDocs?.forEach((doc: { id: number; folder_id: number }) => {
      documentSourceMap.set(String(doc.id), doc.folder_id);
    });
  }

  // Directly selected documents that aren't already mapped via a shared folder
  // get NULL (shown at the share's root).
  documentIds.forEach((docId) => {
    if (!documentSourceMap.has(docId)) {
      documentSourceMap.set(docId, null);
    }
  });

  if (documentSourceMap.size === 0) {
    throw new Error("No documents to share (selected folders may be empty)");
  }

  // Generate token and calculate expiry
  const shareToken = generateShareToken();
  const expiresAt = new Date(Date.now() + expiresInSeconds * 1000).toISOString();

  // Get current user
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    throw new Error("User not authenticated");
  }

  // Get user profile ID
  const { data: profile, error: profileError } = await supabase
    .from("user_profiles")
    .select("id")
    .eq("auth_user_id", user.id)
    .single();

  if (profileError || !profile) {
    throw new Error("User profile not found");
  }

  // Create the share record using the resolved org_id
  const { data: share, error: shareError } = await supabase
    .from("document_shares")
    .insert({
      share_token: shareToken,
      created_by: profile.id,
      building_id: parseInt(buildingId, 10),
      org_id: resolvedOrgId,
      title: title || null,
      expires_at: expiresAt,
      is_revoked: false,
    })
    .select("id")
    .single();

  if (shareError || !share) {
    console.error("Error creating share:", shareError);
    throw new Error(shareError?.message || "Failed to create share");
  }

  // Create share items with source_folder_id tracking
  const shareItems = Array.from(documentSourceMap.entries()).map(([docId, sourceFolderId]) => ({
    share_id: share.id,
    document_id: parseInt(docId, 10),
    source_folder_id: sourceFolderId,
  }));

  const { error: itemsError } = await supabase.from("document_share_items").insert(shareItems);

  if (itemsError) {
    // Rollback: delete the share if items failed
    await supabase.from("document_shares").delete().eq("id", share.id);
    console.error("Error creating share items:", itemsError);
    throw new Error(itemsError.message || "Failed to add documents to share");
  }

  // Record explicitly-shared folder IDs so the recipient sees the same folder
  // root the sharer picked, even when intermediate folders contain only subfolders
  // (and therefore no rows in document_share_items reference them directly).
  if (folderIds.length > 0) {
    const folderRows = folderIds.map((folderId) => ({
      share_id: share.id,
      folder_id: parseInt(folderId, 10),
    }));

    const { error: foldersError } = await supabase
      .from("document_share_folders")
      .insert(folderRows);

    if (foldersError) {
      await supabase.from("document_shares").delete().eq("id", share.id);
      console.error("Error creating share folders:", foldersError);
      throw new Error(foldersError.message || "Failed to add folders to share");
    }
  }

  // Unrounded: the picker's shortest option is 1 hour, and rounding that to whole days
  // reports 0 — indistinguishable from a missing value in a funnel. 0.0417 / 1 / 7 / 30.
  track("share_link_created", {
    expiry_days: expiresInSeconds / 86400,
    // Documents only. A shared folder is already represented by its contents in
    // `shareItems` (the expansion above), so adding the folders on top would count
    // each of them twice over — the rows in `document_share_folders` exist to place
    // those documents, not to add items of their own.
    item_count: shareItems.length,
  });

  // Build the share URL
  const shareUrl = buildShareUrl(shareToken);

  return {
    shareId: share.id,
    shareToken,
    shareUrl,
  };
}

/**
 * List all shares for a building
 */
export async function listShares(buildingId: string): Promise<Share[]> {
  const { data, error } = await supabase
    .from("document_shares")
    .select(
      `
      id,
      share_token,
      title,
      expires_at,
      view_count,
      last_accessed_at,
      is_revoked,
      created_at,
      document_share_items(count)
    `
    )
    .eq("building_id", parseInt(buildingId, 10))
    .eq("is_revoked", false)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error listing shares:", error);
    throw new Error(error.message || "Failed to list shares");
  }

  // Map to Share type with document count
  return (data || []).map((share: Record<string, unknown>) => ({
    id: share.id,
    share_token: share.share_token,
    title: share.title,
    expires_at: share.expires_at,
    view_count: share.view_count || 0,
    last_accessed_at: share.last_accessed_at,
    is_revoked: share.is_revoked || false,
    created_at: share.created_at,
    document_count:
      (share.document_share_items as unknown as [{ count: number }] | undefined)?.[0]?.count || 0,
  }));
}

/**
 * Revoke a share (soft-delete by setting is_revoked flag)
 */
export async function revokeShare(shareId: string): Promise<void> {
  // Get current user for audit trail
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    throw new Error("User not authenticated");
  }

  // Get user profile ID
  const { data: profile, error: profileError } = await supabase
    .from("user_profiles")
    .select("id")
    .eq("auth_user_id", user.id)
    .single();

  if (profileError || !profile) {
    throw new Error("User profile not found");
  }

  const { error } = await supabase
    .from("document_shares")
    .update({
      is_revoked: true,
      revoked_at: new Date().toISOString(),
      revoked_by: profile.id,
    })
    .eq("id", shareId);

  if (error) {
    console.error("Error revoking share:", error);
    throw new Error(error.message || "Failed to revoke share");
  }
}

/**
 * Validate a share token and get shared documents
 * Uses Edge Function for unauthenticated access
 */
export async function validateShare(token: string): Promise<ShareDetails> {
  const { data, error } = await supabase.functions.invoke("validate-share", {
    body: { token },
  });

  if (error) {
    console.error("Error validating share:", error);
    throw new Error(error.message || "Failed to validate share");
  }

  if (!data || !data.success) {
    throw new Error(data?.error || "Invalid share");
  }

  return data.share as ShareDetails;
}

/**
 * Check if a share is still valid (not expired, not revoked)
 */
export function isShareActive(share: Share): boolean {
  if (share.is_revoked) return false;
  const expiresAt = new Date(share.expires_at);
  return expiresAt > new Date();
}

/**
 * Structured "time until expiry" so callers can localise the label.
 * `expired` = already past; otherwise the largest whole unit remaining.
 */
export type ExpiryInfo =
  | { status: "expired" }
  | { status: "remaining"; unit: "day" | "hour" | "minute"; value: number };

/**
 * Compute the time remaining until expiry as structured data.
 * Formatting/translation is the caller's responsibility.
 */
export function getExpiryInfo(expiresAt: string): ExpiryInfo {
  const expiry = new Date(expiresAt);
  const now = new Date();

  if (expiry <= now) {
    return { status: "expired" };
  }

  const diffMs = expiry.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return { status: "remaining", unit: "day", value: diffDays };
  }
  if (diffHours > 0) {
    return { status: "remaining", unit: "hour", value: diffHours };
  }
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  return { status: "remaining", unit: "minute", value: diffMinutes };
}
